/**
 * QA Case Export — Ken 2026-07-10 memo.
 *
 * Workflow: type an artifact ID → see the steps that actually ran →
 * check the ones you want → optionally narrow the L4 fanout →
 * type a case name → export a zip. Ships the assembled bundle
 * straight to the browser's Downloads dir with the same folder
 * shape a Claude debugging session expects (see qa_export.py for the
 * on-disk naming rules).
 *
 * All state stays local (no query cache) — this is a one-shot
 * builder, not a browsing surface. Each new artifact-ID submit
 * hits /qa/artifact-lookup/ once and then /qa/artifact/<id>/steps/
 * once; nothing else fires until Export is clicked.
 */
import { Fragment, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import {
  listThreadSuggestions, applyThreadSuggestion, dismissThreadSuggestion, listThreadSuggestionsDeid,
  listFollowupCorral, type FollowupCorralRow,
  listNavBanners, uploadNavBanner, activateNavBanner, clearNavBanner, deleteNavBanner,
  type ThreadSuggestion, type NavBanner, type DeidThreadResponse,
  listChimeSounds, uploadChimeSoundsZip, deleteChimeSound, renameChimeSound, chimeSoundAudioUrl, type ChimeSound,
  listPatentDocs, patentDocDownloadUrl, type PatentDoc,
} from '../api'
import {
  Loader2, AlertCircle,
  GitMerge, Check, X, RefreshCw, AlertTriangle,
  ArrowRightLeft, Upload, Play, Download,
  Image as ImageIcon, Trash2,
} from 'lucide-react'
import {
  useProductName, saveProductName, FONTS, FONT_GROUPS, PRODUCT_NAME_COLOR,
  type FontKey, type FontGroup,
} from '../productName'
import { useNavColor, saveNavColor, NAV_COLOR_OPTIONS } from '../navColor'
import { saveNavBanner } from '../navBanner'
import { pushGlyph, getGlyph, getContractIntakeAddress, saveContractIntakeAddress, getFontsMap, pushFont, removeFont, applyUploadedFonts, subscribeConfigLoaded } from '../uiConfig'
import { ProductPerformanceSection } from './Stats'
import { useAuth } from '../auth'
import { isPlayheadTenant } from '../playhead'

// Collapsible tool wrapper (Ken 2026-08-23): QA tools default COLLAPSED — click a
// title to expand — so the page reads as a tidy list instead of one long scroll.
// Open state persists per tool in localStorage. Purely presentational; the tool
// internals are unchanged.
const QA_TOOL_OPEN_KEY = 'me.qaToolOpen'
function loadQaOpen(): Record<string, boolean> {
  try { return JSON.parse(localStorage.getItem(QA_TOOL_OPEN_KEY) || '{}') } catch { return {} }
}
function CollapsibleTool({ title, children }: { title: string; children: ReactNode }) {
  const [open, setOpen] = useState<boolean>(() => !!loadQaOpen()[title])
  const toggle = () => setOpen((o) => {
    const next = !o
    try {
      const st = loadQaOpen(); st[title] = next
      localStorage.setItem(QA_TOOL_OPEN_KEY, JSON.stringify(st))
    } catch { /* ignore */ }
    return next
  })
  return (
    <div className="border-b me-border">
      <button
        onClick={toggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2 py-2.5 text-left text-[14px] font-semibold me-text hover:opacity-80"
      >
        <span className={`inline-block text-[11px] transition-transform ${open ? 'rotate-90' : ''} me-text-mute`}>▶</span>
        {title}
      </button>
      {/* Hide (not unmount) when collapsed so each tool keeps its state + any
          background polling — visibility toggles, functionality doesn't change. */}
      <div className={open ? 'pb-5' : 'hidden'}>{children}</div>
    </div>
  )
}

// AI circle image (Ken 2026-10-23, KEN only) — its OWN QA tool, not nested in
// Product Name. Upload a PNG/JPG to use as the surface of the bottom-right AI
// circle button; stored as a data URI in the tenant config (chatBubbleImage).
function AiCircleImageSection() {
  const cfg = useProductName()
  const update = (patch: Partial<typeof cfg>) => saveProductName({ ...cfg, ...patch })
  const [err, setErr] = useState('')
  const MAX_KB = 3072
  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setErr('')
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (!/^image\/(png|jpeg|svg\+xml)$/.test(f.type)) { setErr('Use a PNG or JPG image.'); return }
    if (f.size > MAX_KB * 1024) { setErr(`That image is ${Math.round(f.size / 1024)} KB — keep it under ${MAX_KB} KB.`); return }
    const reader = new FileReader()
    reader.onload = () => { if (typeof reader.result === 'string') update({ chatBubbleImage: reader.result }) }
    reader.onerror = () => setErr('Could not read that file — try another image.')
    reader.readAsDataURL(f)
  }
  return (
    <div>
      <p className="mb-3 text-[12px] me-text-mute">
        The round AI button in the lower-right corner. Upload a PNG or JPG to use as its surface; remove it to go back to the plain coloured circle.
      </p>
      {cfg.chatBubbleImage && (
        <div className="mb-2 flex items-center gap-3">
          <span className="inline-flex h-10 w-10 overflow-hidden rounded-full border me-border bg-cover bg-center"
            style={{ backgroundImage: `url("${cfg.chatBubbleImage}")` }} />
          <button type="button" onClick={() => { setErr(''); update({ chatBubbleImage: undefined }) }}
            className="me-btn me-btn-outline text-[12px]">Remove image</button>
        </div>
      )}
      <input type="file" accept="image/png,image/jpeg" onChange={onFile}
        className="block text-[12px] me-text-mute file:me-btn file:me-btn-outline file:mr-2 file:text-[12px]" />
      <p className="mt-1 text-[10px] me-text-mute">PNG or JPG, shown in a 40&nbsp;px circle (a square image works best). Under {MAX_KB}&nbsp;KB.</p>
      {err && (
        <div className="mt-1 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
          <AlertCircle size={13} /> {err}
        </div>
      )}
    </div>
  )
}

// Custom fonts (Ken 2026-10-27, KEN only). Upload a self-hosted web font; it's
// stored as a data URI on the tenant config and registered as an @font-face by
// applyUploadedFonts, so the family name becomes usable anywhere (theme vars and
// the coming per-voice assignments). Mirrors the AI-circle image upload.
function CustomFontsSection() {
  const [fonts, setFonts] = useState<Record<string, string>>(() => getFontsMap())
  const [name, setName] = useState('')
  const [err, setErr] = useState('')
  const MAX_KB = 1024
  // The tenant config loads asynchronously (App.tsx), usually AFTER this panel
  // mounts — so the initial getFontsMap() can be empty on a fresh page load.
  // Re-read whenever the config (re)loads so the list repopulates. (Ken 2026-10-27)
  useEffect(() => subscribeConfigLoaded(() => setFonts(getFontsMap())), [])
  function onFile(e: ChangeEvent<HTMLInputElement>) {
    setErr('')
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    if (!/\.(woff2?|ttf|otf)$/i.test(f.name) && !/^font\//.test(f.type)) {
      setErr('Use a .woff2 (preferred), .woff, .ttf, or .otf font file.'); return
    }
    if (f.size > MAX_KB * 1024) {
      setErr(`That file is ${Math.round(f.size / 1024)} KB — keep each font under ${MAX_KB} KB (a .woff2 is usually well under).`); return
    }
    const fam = (name.trim() || f.name.replace(/\.(woff2?|ttf|otf)$/i, '')).replace(/['\\]/g, '').trim()
    if (!fam) { setErr('Give the font a name first.'); return }
    if (fonts[fam]) { setErr(`A font named "${fam}" already exists — remove it first or use a different name.`); return }
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result !== 'string') return
      const next = { ...fonts, [fam]: reader.result }
      setFonts(next); pushFont(fam, reader.result); applyUploadedFonts(next); setName('')
    }
    reader.onerror = () => setErr('Could not read that file — try another.')
    reader.readAsDataURL(f)
  }
  function remove(fam: string) {
    setErr('')
    const next = { ...fonts }; delete next[fam]
    setFonts(next); removeFont(fam); applyUploadedFonts(next)
  }
  const names = Object.keys(fonts)
  return (
    <div>
      <p className="mb-3 text-[12px] me-text-mute">
        Upload a self-hosted web font (a <span className="me-mono">.woff2</span> file is best). Name it, and
        the name becomes a face the app can use. Nothing changes on screen until a font is assigned to a voice
        or a slot — this just makes the face available.
      </p>
      {names.length > 0 && (
        <div className="mb-3 space-y-2">
          {names.map((fam) => (
            <div key={fam} className="flex items-center gap-3 rounded-[5px] border me-border px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-[11px] me-text-mute">{fam}</div>
                <div className="truncate text-[18px] me-text" style={{ fontFamily: `'${fam}'` }}>
                  The quick brown fox — 0123 · M-834
                </div>
              </div>
              <button type="button" onClick={() => remove(fam)} className="me-btn me-btn-outline shrink-0 text-[12px]">Remove</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="Font name (e.g. Spectral)"
          className="me-input text-[12px]" style={{ width: 220 }} />
        <input type="file" accept=".woff2,.woff,.ttf,.otf,font/woff2,font/woff,font/ttf,font/otf" onChange={onFile}
          className="block text-[12px] me-text-mute file:me-btn file:me-btn-outline file:mr-2 file:text-[12px]" />
      </div>
      <p className="mt-1 text-[10px] me-text-mute">
        Each font under {MAX_KB}&nbsp;KB. Must be licensed for web/self-hosting (OFL or Google Fonts are safe).
        If you leave the name blank it's taken from the filename.
      </p>
      {err && (
        <div className="mt-1 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
          <AlertCircle size={13} /> {err}
        </div>
      )}
    </div>
  )
}

export default function QA() {
  const qaAuth = useAuth()
  const qaIsKen = qaAuth.status === 'authenticated' && isPlayheadTenant(qaAuth.user.client?.code)
  // Stamp 'last opened QA' so StudioLayout's nav pip knows what's new since this
  // visit — on open AND on leave, so a check landing while viewing still clears
  // once seen. Mirrors the Searches page. (Ken 2026-10-20.)
  useEffect(() => {
    const stamp = () => { try { localStorage.setItem('me.qa.seenAt', new Date().toISOString()) } catch { /* ignore */ } }
    stamp()
    return stamp
  }, [])
  return (
    // Outer scroll wrapper so the page scrolls at normal browser zoom.
    // StudioLayout sets <main overflow-hidden>, so the page itself
    // provides the scroll surface.
    <div className="h-full overflow-y-auto me-scroll">
    <div className="mx-auto max-w-4xl p-6">
      <h1 className="mb-1 text-[18px] font-semibold me-text">QA</h1>

      <CollapsibleTool title="Control Bar Banner"><NavBannerSection /></CollapsibleTool>
      <CollapsibleTool title="Contract Intake Address"><ContractIntakeSection /></CollapsibleTool>
      <CollapsibleTool title="Agreement Queue Icon"><GlyphUploader configKey="agreement_queue_glyph" buttonLabel="Agreement Queue" /></CollapsibleTool>
      <CollapsibleTool title="Chime Sounds"><ChimeSoundsSection /></CollapsibleTool>
      <CollapsibleTool title="Patent Docs"><PatentDocsSection /></CollapsibleTool>
      <CollapsibleTool title="Thread-issue check"><FireworksCheckQueue /><ThreadSuggestionsPanel /></CollapsibleTool>
      <CollapsibleTool title="Follow-up corral"><FollowupCorralPanel /></CollapsibleTool>
      <CollapsibleTool title="Product Name"><ProductNameSection /></CollapsibleTool>
      {qaIsKen && <CollapsibleTool title="AI circle image"><AiCircleImageSection /></CollapsibleTool>}
      {qaIsKen && <CollapsibleTool title="Custom fonts"><CustomFontsSection /></CollapsibleTool>}
      <CollapsibleTool title="Product performance"><ProductPerformanceSection /></CollapsibleTool>
    </div>
    </div>
  )
}

// Font system — five locked GROUPS (Ken 2026-08-10). Each group picks a
// typeface from the unified FONTS registry; changes apply live everywhere via
// the CSS vars published in Layout.tsx. A single font-picker row, previewing
// each option in its own face.
function FontRow({ label, blurb, value, onPick }: {
  label: string; blurb?: string; value: FontKey; onPick: (f: FontKey) => void
}) {
  const keys = Object.keys(FONTS) as FontKey[]
  return (
    <div>
      <label className="mb-1 block text-[11px] me-text-mute">
        <span className="me-text font-medium">{label}</span>
        {blurb && <span className="ml-1.5">— {blurb}</span>}
      </label>
      <div className="flex flex-wrap gap-2">
        {keys.map((k) => (
          <button key={k} onClick={() => onPick(k)} data-selected={value === k ? 'true' : undefined}
            className="me-tab !rounded-[5px] border me-border px-3 py-1.5 text-[14px]" style={{ fontFamily: FONTS[k].family }}>
            {FONTS[k].label}
          </button>
        ))}
      </div>
    </div>
  )
}
function SizeRow({ label, value, onChange }: { label: string; value: number; onChange: (n: number) => void }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] me-text-mute">{label} <span className="me-mono">{value}px</span></label>
      <input type="range" min={10} max={48} step={1} value={value} onChange={(e) => onChange(Number(e.target.value))} className="w-64 align-middle" />
    </div>
  )
}

// The store field each group reads/writes for its font.
const GROUP_FONT_FIELD: Record<FontGroup, 'brandFont' | 'recordFont' | 'summaryFont' | 'chromeFont' | 'dataFont'> = {
  brand: 'brandFont', record: 'recordFont', summary: 'summaryFont', chrome: 'chromeFont', data: 'dataFont',
}

function ProductNameSection() {
  const cfg = useProductName()
  const update = (patch: Partial<typeof cfg>) => saveProductName({ ...cfg, ...patch })
  const navColor = useNavColor()
  const [logoErr, setLogoErr] = useState('')
  // Logo-size cap. KEN/playhead gets a larger cap (Ken 2026-10-22 — a ~1.5 MB
  // JPG wordmark); MOVU/real tenants keep the 500 KB guardrail so a heavy logo
  // can't bloat the config every user fetches.
  const auth = useAuth()
  const isKen = auth.status === 'authenticated' && isPlayheadTenant(auth.user.client?.code)
  const logoMaxKb = isKen ? 3072 : 500

  // Upload a logo image → store it as a data URI inside the tenant config
  // (rides the same server sync as the rest of the wordmark settings). PNG/JPG
  // only — a PDF can't be shown inline where the wordmark goes.
  function onLogoFile(e: ChangeEvent<HTMLInputElement>) {
    setLogoErr('')
    const f = e.target.files?.[0]
    e.target.value = '' // let the same file be re-picked after a removal
    if (!f) return
    if (!/^image\/(png|jpeg|svg\+xml)$/.test(f.type)) {
      setLogoErr('Use a PNG or JPG image (a PDF can’t be displayed as a logo).')
      return
    }
    const MAX = logoMaxKb * 1024
    if (f.size > MAX) {
      setLogoErr(`That image is ${Math.round(f.size / 1024)} KB — keep it under ${logoMaxKb} KB.`)
      return
    }
    const reader = new FileReader()
    reader.onload = () => { if (typeof reader.result === 'string') update({ brandLogo: reader.result }) }
    reader.onerror = () => setLogoErr('Could not read that file — try another export.')
    reader.readAsDataURL(f)
  }

  return (
    <>
      <section className="mb-6">
        <p className="mb-3 text-[12px] me-text-mute">
          Try names, fonts, and sizes for the wordmark in the top nav bar — changes
          apply live. Color is fixed at <span className="me-mono">{PRODUCT_NAME_COLOR}</span>.
        </p>
        <div className="rounded-md border me-border p-4 space-y-4">
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Wordmark (upper-left)</label>
            <input
              value={cfg.name}
              onChange={(e) => update({ name: e.target.value })}
              placeholder="Matters"
              className="me-input text-[13px]"
              disabled={!!cfg.brandLogo}
            />
            {cfg.brandLogo && (
              <p className="mt-1 text-[10px] me-text-mute">
                A logo image is set, so the text wordmark is hidden. Remove the logo to type a name again.
              </p>
            )}
          </div>

          {/* Logo image (Ken 2026-09-10) — upload a PNG/JPG (e.g. from Miro) to
              replace the text wordmark. Stored in the tenant config as a data
              URI so every user of the tenant sees it. */}
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Logo image (replaces the wordmark)</label>
            {cfg.brandLogo && (
              <div className="mb-2 flex items-center gap-3">
                <span
                  className="inline-flex items-center rounded border me-border px-2 py-1"
                  style={{ background: navColor }}
                >
                  <img
                    src={cfg.brandLogo}
                    alt="logo preview"
                    style={{ height: Math.round(cfg.brandSize * 1.5), width: 'auto', maxWidth: 280, objectFit: 'contain' }}
                  />
                </span>
                <button
                  type="button"
                  onClick={() => { setLogoErr(''); update({ brandLogo: undefined }) }}
                  className="me-btn me-btn-outline text-[12px]"
                >
                  Remove logo
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={onLogoFile}
              className="block text-[12px] me-text-mute file:me-btn file:me-btn-outline file:mr-2 file:text-[12px]"
            />
            <p className="mt-1 text-[10px] me-text-mute">
              PNG or JPG (transparent PNG recommended), a narrow rectangle to fit the nav bar. Under {logoMaxKb}&nbsp;KB.
              A PDF can’t be displayed as a logo — export an image from Miro instead. The
              <span className="me-mono"> Size — wordmark</span> slider controls its height.
            </p>
            {logoErr && (
              <div className="mt-1 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
                <AlertCircle size={13} /> {logoErr}
              </div>
            )}
          </div>

          {/* Five locked font groups (Ken 2026-08-10). Brand also owns the
              wordmark size; the other four are typeface-only. */}
          {FONT_GROUPS.map((g) => (
            <div key={g.key} className="space-y-2">
              <FontRow
                label={g.label}
                blurb={g.blurb}
                value={cfg[GROUP_FONT_FIELD[g.key]] as FontKey}
                onPick={(k) => update({ [GROUP_FONT_FIELD[g.key]]: k })}
              />
              {g.key === 'brand' && (
                <SizeRow label="Size — wordmark" value={cfg.brandSize} onChange={(n) => update({ brandSize: n })} />
              )}
            </div>
          ))}
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Nav page names</label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <div className="mb-0.5 text-[10px] me-text-mute">Main</div>
                <input
                  value={cfg.pageMainName}
                  onChange={(e) => update({ pageMainName: e.target.value })}
                  placeholder="Matters"
                  className="me-input text-[13px] w-full"
                />
              </div>
              <div>
                <div className="mb-0.5 text-[10px] me-text-mute">Stats</div>
                <input
                  value={cfg.pageStatsName}
                  onChange={(e) => update({ pageStatsName: e.target.value })}
                  placeholder="Stats"
                  className="me-input text-[13px] w-full"
                />
              </div>
              <div>
                <div className="mb-0.5 text-[10px] me-text-mute">Playbook</div>
                <input
                  value={cfg.pagePlaybookName}
                  onChange={(e) => update({ pagePlaybookName: e.target.value })}
                  placeholder="Playbook"
                  className="me-input text-[13px] w-full"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Stats — “Daily Average” heading</label>
            <input
              value={cfg.statsDailyAvgLabel}
              onChange={(e) => update({ statsDailyAvgLabel: e.target.value })}
              placeholder="Daily Averages: Communications Managed & New Matters Instantiated"
              className="me-input text-[13px] w-full"
            />
            <p className="mt-1 text-[10.5px] me-text-mute">
              The heading above the four daily-average mini-boxes on the Stats page.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Sidebar label weight — bold</label>
            <div className="flex flex-col gap-1 text-[12px] me-text">
              <label className="inline-flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={cfg.boldFolder} onChange={(e) => update({ boldFolder: e.target.checked })} className="cursor-pointer" />
                Folders
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={cfg.boldSubfolder} onChange={(e) => update({ boldSubfolder: e.target.checked })} className="cursor-pointer" />
                Sub-folders
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={cfg.boldMatter} onChange={(e) => update({ boldMatter: e.target.checked })} className="cursor-pointer" />
                Matter names
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer w-fit">
                <input type="checkbox" checked={cfg.boldSubject} onChange={(e) => update({ boldSubject: e.target.checked })} className="cursor-pointer" />
                Subject / filename (pane-2 chronology)
              </label>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Identifiers</label>
            <label className="inline-flex items-center gap-2 cursor-pointer w-fit text-[12px] me-text">
              <input type="checkbox" checked={cfg.hideMatterKind} onChange={(e) => update({ hideMatterKind: e.target.checked })} className="cursor-pointer" />
              Hide matter kind when identifiers are shown
            </label>
            <p className="mt-1 text-[10.5px] me-text-mute">
              When identifiers are exposed (Settings → Identifiers), the sidebar
              matter line still shows the code and stage; this drops just the
              kind (e.g. “litigation”). On by default.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Selected accent color</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={cfg.selectedColor}
                onChange={(e) => update({ selectedColor: e.target.value })}
                className="h-7 w-10 cursor-pointer rounded-sm border me-border bg-transparent p-0.5"
              />
              <span className="me-mono text-[11px] me-text-mute">{cfg.selectedColor}</span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[11px] me-text-mute">Nav bar color</label>
            <div className="space-y-1.5">
              {[NAV_COLOR_OPTIONS.earthy, NAV_COLOR_OPTIONS.neon].map((row, i) => (
                <div key={i} className="flex gap-1.5">
                  {row.map((c) => (
                    <button
                      key={c}
                      onClick={() => saveNavColor(c)}
                      title={c}
                      aria-label={c}
                      className="h-6 w-6 rounded-sm"
                      style={{
                        background: c,
                        boxShadow: navColor.toLowerCase() === c.toLowerCase()
                          ? '0 0 0 2px #14110E, 0 0 0 3px #fff inset'
                          : '0 0 0 1px rgba(0,0,0,0.15)',
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-dashed me-border p-4" style={{ background: navColor }}>
            <div className="mb-2 text-[10px] uppercase tracking-wider" style={{ color: 'rgba(20,17,14,0.5)' }}>
              Preview (on the nav background)
            </div>
            <div
              className="whitespace-nowrap leading-none"
              style={{ fontFamily: FONTS[cfg.brandFont].family, fontSize: cfg.brandSize, color: PRODUCT_NAME_COLOR }}
            >
              {cfg.name || 'Matters'}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

// Control Bar Banner (Emerson 2026-09-18) — upload an image to sit behind the
// Layout header. Newest upload auto-activates; every upload is kept in a
// re-selectable history. The ACTIVE banner is mirrored into the tenant
// ui_config via saveNavBanner so the header shows it live for every user.
// Chime Sounds (Ken 2026-10-19) — upload a .zip of audio files → the firm's Quiet
// chime library. Replaces the current set. Users pick one in View & Sounds.
function ChimeSoundsSection() {
  const [sounds, setSounds] = useState<ChimeSound[]>([])
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const load = () => { listChimeSounds().then(setSounds).catch(() => setSounds([])) }
  useEffect(() => { load() }, [])
  async function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (!f) return
    setBusy(true); setMsg(null)
    try {
      const r = await uploadChimeSoundsZip(f)
      setSounds(r.sounds)
      setMsg(`Loaded ${r.sounds.length} sound(s)${r.skipped.length ? `; skipped ${r.skipped.length}` : ''}.`)
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setMsg(detail || 'Upload failed.')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }
  function demo(id: number) {
    try { const a = new Audio(chimeSoundAudioUrl(id)); a.volume = 0.6; void a.play().catch(() => {}) } catch { /* ignore */ }
  }
  async function remove(id: number) { await deleteChimeSound(id); load() }
  async function saveName(id: number, name: string) {
    const r = await renameChimeSound(id, name.trim())
    setSounds((cur) => cur.map((s) => (s.id === id ? { ...s, name: r.name } : s)))
  }
  return (
    <div>
      <p className="mb-2 text-[12px] me-text-mute">
        Upload a <span className="me-mono">.zip</span> of audio files (.wav / .mp3 / .ogg). This <b>replaces</b>{' '}
        the firm's chime library. Order by a number in the filename (<span className="me-mono">1_jfuso.wav</span>,
        {' '}<span className="me-mono">2_chime7.wav</span>…). Type a <b>name</b> for each — that's what users see in
        Settings → Chimes, where they pick one and Quiet plays it.
      </p>
      <label className="me-btn me-btn-outline inline-flex cursor-pointer items-center gap-1 text-[12px]">
        <Upload size={13} /> {busy ? 'Uploading…' : 'Upload .zip'}
        <input ref={fileRef} type="file" accept=".zip,application/zip" className="hidden" onChange={onUpload} disabled={busy} />
      </label>
      {msg && <span className="ml-2 text-[11px] me-text-mute">{msg}</span>}
      <div className="mt-3 flex flex-col gap-1.5">
        {sounds.length === 0 && <div className="text-[11px] me-text-mute">No chimes uploaded.</div>}
        {sounds.map((s) => (
          <div key={s.id} className="flex items-center gap-2 text-[12px]">
            <button onClick={() => demo(s.id)} title="Hear it" className="shrink-0 me-text-mute hover:me-text"><Play size={12} /></button>
            <ChimeNameInput value={s.name} onSave={(v) => saveName(s.id, v)} />
            <span className="me-mono shrink-0 text-[10px] me-text-mute" title={s.filename}>{s.filename}</span>
            <button onClick={() => remove(s.id)} title="Remove" className="shrink-0 me-text-mute hover:me-text"><Trash2 size={11} /></button>
          </div>
        ))}
      </div>
    </div>
  )
}

// Editable display name for a chime — saves on blur or Enter (Ken types the name
// that appears "burned into" Settings → Chimes).
function ChimeNameInput({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value)
  useEffect(() => { setV(value) }, [value])
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v.trim() && v.trim() !== value) onSave(v) }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
      placeholder="name"
      className="me-input flex-1 !py-0.5 text-[12px]"
    />
  )
}

// Patent Docs (Ken 2026-10-20) — studio-only download area for the provisional-
// patent context docs under docs/patent/ (dynamic: lists whatever .md is there).
function PatentDocsSection() {
  const [docs, setDocs] = useState<PatentDoc[]>([])
  useEffect(() => { listPatentDocs().then(setDocs).catch(() => setDocs([])) }, [])
  return (
    <div>
      <p className="mb-2 text-[12px] me-text-mute">
        Download the patent-context docs (markdown from <span className="me-mono">docs/patent/</span>).
      </p>
      <div className="flex flex-col gap-1">
        {docs.length === 0 && <div className="text-[11px] me-text-mute">No patent docs.</div>}
        {docs.map((d) => (
          <a key={d.key} href={patentDocDownloadUrl(d.key)} download
            className="me-btn me-btn-outline inline-flex items-center gap-1.5 text-[12px]">
            <Download size={13} /> {d.filename}
            <span className="me-mono text-[10px] me-text-mute">({Math.round(d.bytes / 1024)} KB)</span>
          </a>
        ))}
      </div>
    </div>
  )
}

function GlyphUploader({ configKey, buttonLabel }: { configKey: string; buttonLabel: string }) {
  const [glyph, setGlyph] = useState<string | null>(() => getGlyph(configKey))
  const [err, setErr] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (fileRef.current) fileRef.current.value = ''  // allow re-picking the same file
    if (!f) return
    setErr('')
    if (f.type !== 'image/png' && f.type !== 'image/svg+xml') { setErr('Please choose a PNG or SVG.'); return }
    if (f.size > 500_000) { setErr('File is over 500 KB — please use a smaller one.'); return }
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader()
        r.onload = () => (typeof r.result === 'string' ? res(r.result) : rej(new Error('read failed')))
        r.onerror = () => rej(new Error('read failed'))
        r.readAsDataURL(f)
      })
      pushGlyph(configKey, dataUrl)
      setGlyph(dataUrl)
    } catch (e: any) {
      setErr(e?.message || 'Upload failed')
    }
  }

  return (
    <section className="mb-6">
      <p className="mb-3 text-[12px] me-text-mute">
        Upload a small PNG or SVG to use as the icon on the <b>{buttonLabel}</b> button. Square works
        best — it renders at 16×16. Shared tenant-wide; refresh to see it. Max 500&nbsp;KB.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept="image/png,image/svg+xml" onChange={onFile} className="hidden" />
        <button onClick={() => fileRef.current?.click()}
          className="me-btn me-btn-primary text-[12px] flex items-center gap-1.5">
          <Upload size={13} /> Upload PNG / SVG
        </button>
        <button onClick={() => { pushGlyph(configKey, null); setGlyph(null) }} disabled={!glyph}
          className="me-btn me-btn-outline text-[12px] disabled:opacity-40">Remove</button>
        {/* live preview in a mock button */}
        <span className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[13px] font-medium"
          style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
          {glyph
            ? <img src={glyph} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
            : <span style={{ display: 'inline-block', width: 16, height: 16, border: '1px dashed var(--me-border)' }} />}
          {buttonLabel}
        </span>
      </div>
      {err && (
        <div className="mt-2 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
          <AlertCircle size={13} /> {err}
        </div>
      )}
    </section>
  )
}

function ContractIntakeSection() {
  const [addr, setAddr] = useState(() => getContractIntakeAddress())
  const [saved, setSaved] = useState(false)
  return (
    <section className="mb-6">
      <p className="mb-3 text-[12px] me-text-mute">
        The dedicated address a contract submission must name in <b>To</b> or <b>Cc</b> to be treated
        as a submission (e.g. <span className="me-mono">contracts@moravian.edu</span>). When Maria or
        Heather forward material here, the forward still counts because this address is in the chain.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input value={addr} onChange={(e) => { setAddr(e.target.value); setSaved(false) }}
          placeholder="contracts@moravian.edu"
          className="rounded-md px-3 py-2 text-[13px] w-80"
          style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }} />
        <button onClick={() => { saveContractIntakeAddress(addr.trim()); setSaved(true) }}
          className="me-btn me-btn-primary text-[12px]">Save</button>
        {saved && <span className="text-[12px] me-text-mute">saved</span>}
      </div>
    </section>
  )
}


function NavBannerSection() {
  const [banners, setBanners] = useState<NavBanner[]>([])
  const [activeId, setActiveId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  // Reflect a server payload into local state AND the live header (or clear it).
  function apply(p: { active: NavBanner | null; banners: NavBanner[] }) {
    setBanners(p.banners)
    setActiveId(p.active ? p.active.id : null)
    saveNavBanner(p.active ? p.active.data_url : null)
  }

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const p = await listNavBanners()
        if (alive) { setBanners(p.banners); setActiveId(p.active ? p.active.id : null) }
      } catch (e: any) {
        if (alive) setErr(e?.response?.data?.detail || e?.message || 'Failed to load banners')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (fileRef.current) fileRef.current.value = ''  // allow re-picking the same file
    if (!f) return
    setErr('')
    if (!/^image\//.test(f.type)) { setErr('Please choose an image file (PNG, JPG, or WebP).'); return }
    if (f.size > 2_000_000) { setErr('Image is over 2 MB — please use a smaller one.'); return }
    setBusy(true)
    try {
      const dataUrl = await new Promise<string>((res, rej) => {
        const r = new FileReader()
        r.onload = () => (typeof r.result === 'string' ? res(r.result) : rej(new Error('read failed')))
        r.onerror = () => rej(new Error('read failed'))
        r.readAsDataURL(f)
      })
      // Natural pixel dimensions for the history readout (best-effort).
      const dims = await new Promise<{ w: number | null; h: number | null }>((res) => {
        const img = new window.Image()
        img.onload = () => res({ w: img.naturalWidth, h: img.naturalHeight })
        img.onerror = () => res({ w: null, h: null })
        img.src = dataUrl
      })
      apply(await uploadNavBanner({
        data_url: dataUrl, filename: f.name, content_type: f.type,
        width: dims.w, height: dims.h, byte_size: f.size,
      }))
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Upload failed')
    } finally {
      setBusy(false)
    }
  }

  async function act(fn: () => Promise<{ active: NavBanner | null; banners: NavBanner[] }>) {
    setErr(''); setBusy(true)
    try { apply(await fn()) }
    catch (e: any) { setErr(e?.response?.data?.detail || e?.message || 'Action failed') }
    finally { setBusy(false) }
  }

  const active = banners.find((b) => b.id === activeId) || null

  return (
    <div className="mt-10">
      <section className="mb-6">
        <p className="mb-3 text-[12px] me-text-mute">
          Upload an image to sit behind the top control bar (the header — today just the
          nav colour). The newest upload applies automatically; every one is kept below so you
          can re-select it anytime. For a clean fit, match the bar's shape — a wide, short image
          (the bar is roughly <span className="me-mono">48px</span> tall and full-window wide);
          it's scaled to <span className="me-mono">cover</span> and centered. Shared tenant-wide,
          so Heather &amp; Maria see it too. Max 2&nbsp;MB.
        </p>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp"
            onChange={onFile} className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="me-btn me-btn-primary text-[12px] flex items-center gap-1.5 disabled:opacity-40">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            Upload banner
          </button>
          <button onClick={() => act(clearNavBanner)} disabled={busy || activeId == null}
            className="me-btn me-btn-outline text-[12px] disabled:opacity-40">
            Revert to colour
          </button>
        </div>

        {err && (
          <div className="mt-2 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
            <AlertCircle size={13} /> {err}
          </div>
        )}

        {/* Live preview of the active banner in a mock control bar */}
        <div className="mt-4">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider me-text-mute">
            Active {active ? '' : '— none (control bar shows its colour)'}
          </div>
          <div
            className="flex h-12 items-center rounded-md border me-border px-3 text-[12px]"
            style={{
              backgroundColor: 'var(--me-nav-color)',
              backgroundImage: active ? `url("${active.data_url}")` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <span className="rounded bg-black/40 px-1.5 py-0.5 text-white">
              {active ? (active.filename || `banner #${active.id}`) : 'control-bar preview'}
            </span>
          </div>
        </div>

        {/* Re-selectable history */}
        <div className="mt-5">
          <div className="mb-2 text-[11px] font-medium uppercase tracking-wider me-text-mute">
            History {banners.length ? `(${banners.length})` : ''}
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-[12px] me-text-mute">
              <Loader2 size={13} className="animate-spin" /> Loading…
            </div>
          ) : banners.length === 0 ? (
            <p className="flex items-center gap-1.5 text-[12px] me-text-mute">
              <ImageIcon size={13} /> Nothing uploaded yet.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {banners.map((b) => (
                <div key={b.id} className="rounded-md border p-2 text-[11px] me-text-mute"
                  style={{ borderColor: b.id === activeId ? 'var(--me-accent-select)' : 'var(--me-border)' }}>
                  <div className="mb-1.5 h-10 rounded"
                    style={{
                      backgroundImage: `url("${b.data_url}")`, backgroundSize: 'cover',
                      backgroundPosition: 'center', backgroundColor: 'var(--me-nav-color)',
                    }} />
                  <div className="truncate me-text" title={b.filename}>{b.filename || `banner #${b.id}`}</div>
                  <div className="truncate">
                    {b.width && b.height ? `${b.width}×${b.height}` : '—'}
                    {' · '}{Math.round((b.byte_size || 0) / 1024)} KB
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    {b.id === activeId ? (
                      <span className="me-chip inline-flex items-center gap-1 text-[10px]"><Check size={11} /> active</span>
                    ) : (
                      <button onClick={() => act(() => activateNavBanner(b.id))} disabled={busy}
                        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 disabled:opacity-40">Use</button>
                    )}
                    <button onClick={() => act(() => deleteNavBanner(b.id))} disabled={busy}
                      className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 disabled:opacity-40"
                      title="Remove from history"><Trash2 size={11} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

// ── Content-free followup corral (A′) monitor: held + suppressed outcomes ──────
const CORRAL_ACTIONS = ['all', 'hold', 'suppress'] as const
function FollowupCorralPanel() {
  const [rows, setRows] = useState<FollowupCorralRow[]>([])
  const [counts, setCounts] = useState<{ hold: number; suppress: number }>({ hold: 0, suppress: 0 })
  const [filter, setFilter] = useState<string>('all')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string>('')

  async function load() {
    setLoading(true)
    try {
      const r = await listFollowupCorral(filter)
      setRows(r.rows); setCounts(r.counts); setErr('')
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'failed to load')
    } finally { setLoading(false) }
  }
  useEffect(() => {
    load()
    const t = setInterval(load, 8000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  return (
    <>
      <div className="mb-2 flex items-center gap-2 text-[11px] me-text-mute">
        <span>hold <span className="me-mono">{counts.hold}</span> · suppress <span className="me-mono">{counts.suppress}</span></span>
        {loading && <span>loading…</span>}
      </div>
      <>
          <div className="mb-2 flex items-center gap-2 text-[12px]">
            {CORRAL_ACTIONS.map((a) => (
              <button
                key={a}
                onClick={() => setFilter(a)}
                className={'rounded px-2 py-0.5 ' + (filter === a
                  ? 'bg-[color:var(--me-selected)] me-text' : 'me-text-mute hover:me-text')}
              >
                {a}
              </button>
            ))}
            <span className="me-text-mute ml-auto text-[11px]">auto-refresh 8s</span>
          </div>
          {err && <div className="mb-2 text-[12px] text-[color:var(--me-urgent)]">{err}</div>}
          {rows.length === 0 && !loading ? (
            <div className="me-text-mute px-1 py-2 text-[12px] italic">
              Nothing held or suppressed. Attached followups file straight to their thread's
              matter and aren't shown here.
            </div>
          ) : (
            <table className="w-full text-[12px]">
              <thead className="bg-[color:var(--me-bg)]">
                <tr>
                  <th className="border me-border px-2 py-1 text-left">EMAL</th>
                  <th className="border me-border px-2 py-1 text-left">Kind</th>
                  <th className="border me-border px-2 py-1 text-left">Outcome</th>
                  <th className="border me-border px-2 py-1 text-left">Subject</th>
                  <th className="border me-border px-2 py-1 text-left">From</th>
                  <th className="border me-border px-2 py-1 text-left">Thread matters</th>
                  <th className="border me-border px-2 py-1 text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.artifact_id}>
                    <td className="me-mono border me-border px-2 py-1">{r.emal}</td>
                    <td className="border me-border px-2 py-1">{r.kind}</td>
                    <td className="border me-border px-2 py-1">
                      <span className={r.action === 'hold'
                        ? 'text-[color:var(--me-nav-color)]' : 'me-text-mute'}>{r.action}</span>
                    </td>
                    <td className="border me-border px-2 py-1">{r.subject.slice(0, 64)}</td>
                    <td className="border me-border px-2 py-1">{r.from_email}</td>
                    <td className="border me-border px-2 py-1">
                      {r.thread_matters.length === 0
                        ? <span className="me-text-mute">— none on thread —</span>
                        : r.thread_matters
                            .map((m) => `M-${String(m.client_seq).padStart(3, '0')}`).join(', ')}
                    </td>
                    <td className="me-mono border me-border px-2 py-1 text-[11px]">
                      {r.source_date ? new Date(r.source_date).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
    </>
  )
}


const TS_STATUSES = ['pending', 'applied', 'dismissed', 'auto_applied', 'all'] as const

// De-identified Fireworks queue (Ken 2026-10-18): the cordon-safe view of
// thread-issue checks — ids/scores only, no names/subjects/content — with an
// alarm (N awaiting) and a one-click copy of the task manifest to paste into a
// Fireworks slot. The PII card panel below is unchanged.
function FireworksCheckQueue() {
  const [data, setData] = useState<DeidThreadResponse | null>(null)
  const [status, setStatus] = useState<string>('pending')
  const [flash, setFlash] = useState<string>('')
  // Apply/Dismiss moved up into this de-identified rectangle (Ken 2026-11-01). They
  // act on the suggestion id, which is this table's check_id, so wiring is exact.
  const [busyId, setBusyId] = useState<number | null>(null)
  const [dismissingId, setDismissingId] = useState<number | null>(null)
  const [dismissReason, setDismissReason] = useState('')
  async function load() {
    try { setData(await listThreadSuggestionsDeid(status)) } catch { /* ignore */ }
  }
  async function onApply(id: number) {
    setBusyId(id)
    try { await applyThreadSuggestion(id); await load() }
    catch (e: any) { setFlash(e?.response?.data?.detail || 'apply failed'); setTimeout(() => setFlash(''), 4000) }
    finally { setBusyId(null) }
  }
  async function onDismiss(id: number, reason: string) {
    setBusyId(id)
    try { await dismissThreadSuggestion(id, reason); setDismissingId(null); setDismissReason(''); await load() }
    catch (e: any) { setFlash(e?.response?.data?.detail || 'dismiss failed'); setTimeout(() => setFlash(''), 4000) }
    finally { setBusyId(null) }
  }
  useEffect(() => {
    load()
    const t = setInterval(load, 8000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])
  const rows = data?.suggestions ?? []
  const pending = data?.counts?.pending ?? 0
  async function copyBundle() {
    try {
      await navigator.clipboard.writeText(data?.bundle || '')
      setFlash(`Copied ${rows.length} check${rows.length === 1 ? '' : 's'} — paste into a Fireworks slot.`)
      setTimeout(() => setFlash(''), 4000)
    } catch { setFlash('copy failed — select the bundle text manually') }
  }
  return (
    <div className="me-card p-3 mb-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="me-section-h mr-1">Fireworks queue</h3>
        {pending > 0 && (
          <span className="me-chip text-[10px] uppercase" title="checks awaiting review">⚠ {pending} awaiting</span>
        )}
        <select className="rounded border me-border bg-transparent px-1 py-0.5 text-[11px]"
                value={status} onChange={(e) => setStatus(e.target.value)}>
          {TS_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="me-btn me-btn-outline !px-2 !py-0.5 !text-[11px]"
                onClick={copyBundle} disabled={!rows.length}>
          📋 Copy bundle ({rows.length})
        </button>
        {flash && <span className="me-text-mute text-[11px]">{flash}</span>}
      </div>
      <div className="overflow-auto">
        <table className="w-full table-fixed text-[11px]">
          <thead className="me-text-mute">
            <tr className="border-b me-border text-left">
              <th className="px-2 py-1 w-[64px] font-normal">Check</th>
              <th className="px-2 py-1 w-[120px] font-normal">Email</th>
              <th className="px-2 py-1 font-normal">Current → Target</th>
              <th className="px-2 py-1 w-[84px] font-normal">Thread</th>
              <th className="px-2 py-1 w-[84px] font-normal">Conf/Id</th>
              <th className="px-2 py-1 w-[80px] font-normal">Status</th>
              <th className="px-2 py-1 w-[168px] font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.check_id}>
              <tr className="border-t me-border align-top">
                <td className="me-mono px-2 py-1">#{r.check_id}</td>
                <td className="me-mono px-2 py-1">{r.emal}</td>
                <td className="me-mono px-2 py-1 truncate">
                  {r.current_matter || '—'} <span className="me-text-mute">→</span> {r.target_matter}
                </td>
                <td className="me-mono px-2 py-1">{r.thread_in_target}/{r.thread_total}</td>
                <td className="me-mono px-2 py-1">
                  {r.confidence != null ? r.confidence.toFixed(2) : '—'}/{r.identity != null ? r.identity.toFixed(2) : '—'}
                </td>
                <td className="px-2 py-1">{r.status}</td>
                <td className="px-2 py-1">
                  {r.status === 'pending' && (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => onApply(r.check_id)}
                        disabled={busyId === r.check_id}
                        className="me-btn me-btn-primary flex items-center gap-1 text-[11px] disabled:opacity-40"
                        title={r.type === 'misfiled'
                          ? "Move this email to the thread's matter"
                          : 'Merge into the existing matter'}
                      >
                        {busyId === r.check_id ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        Apply
                      </button>
                      <button
                        onClick={() => { setDismissingId(dismissingId === r.check_id ? null : r.check_id); setDismissReason('') }}
                        disabled={busyId === r.check_id}
                        className="me-btn flex items-center gap-1 text-[11px] disabled:opacity-40"
                        title="Reject this suggestion"
                      >
                        <X size={12} /> Dismiss
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              {dismissingId === r.check_id && (
                <tr>
                  <td colSpan={7} className="px-2 pb-2">
                    <div className="flex items-start gap-2">
                      <textarea
                        autoFocus
                        rows={2}
                        value={dismissReason}
                        onChange={(e) => setDismissReason(e.target.value)}
                        placeholder="Why is this merge wrong? (optional — accumulates as tuning signal for the check)"
                        className="me-input flex-1 text-[12px]"
                      />
                      <div className="flex shrink-0 flex-col gap-1">
                        <button
                          onClick={() => onDismiss(r.check_id, dismissReason)}
                          disabled={busyId === r.check_id}
                          className="me-btn me-btn-primary text-[11px] disabled:opacity-40"
                        >
                          {busyId === r.check_id ? <Loader2 size={12} className="animate-spin" /> : 'Confirm dismiss'}
                        </button>
                        <button
                          onClick={() => { setDismissingId(null); setDismissReason('') }}
                          className="me-btn text-[11px]"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-2 py-2 me-text-mute">No checks for &ldquo;{status}&rdquo;.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function ThreadSuggestionsPanel() {
  const [rows, setRows] = useState<ThreadSuggestion[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [mode, setMode] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string>('')

  async function load() {
    setLoading(true)
    try {
      const r = await listThreadSuggestions(statusFilter)
      setRows(r.suggestions)
      setCounts(r.counts)
      setMode(r.mode)
      setErr('')
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'failed to load suggestions')
    } finally {
      setLoading(false)
    }
  }

  // Refetch on filter change + poll every 8s so it fills in live during a run.
  useEffect(() => {
    load()
    const t = setInterval(load, 8000)
    return () => clearInterval(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  const modeBadge =
    mode === 'act' ? 'me-chip-stale' : mode === 'shadow' ? 'me-chip' : 'me-chip'

  return (
    <>
      <div className="mb-2 flex items-center justify-between text-[11px] me-text-mute">
        <span className={`me-chip ${modeBadge} text-[10px]`}>mode: {mode || '…'}</span>
        <div className="flex items-center gap-3">
          <span>
            {counts.pending ?? 0} pending · {counts.applied ?? 0} applied ·{' '}
            {counts.dismissed ?? 0} dismissed
            {(counts.auto_applied ?? 0) > 0 ? ` · ${counts.auto_applied} auto` : ''}
          </span>
          <button onClick={load} className="hover:me-text" title="Refresh">
            <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      <p className="mb-3 text-[11px] me-text-mute">
        Same-thread duplicates the matcher flagged as near-misses. In{' '}
        <span className="me-mono">shadow</span> nothing was changed — Apply to merge, Dismiss to ignore.
      </p>

      <>
          <div className="mb-3 flex items-center gap-1 text-[11px]">
            {TS_STATUSES.map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`rounded px-2 py-0.5 ${
                  statusFilter === st
                    ? 'bg-[color:var(--me-selected)] me-text'
                    : 'me-text-mute hover:me-text'
                }`}
              >
                {st}
                {st !== 'all' && counts[st] != null ? ` (${counts[st]})` : ''}
              </button>
            ))}
          </div>

          {err && (
            <div className="mb-2 flex items-center gap-2 text-[12px] text-[color:var(--me-urgent)]">
              <AlertCircle size={13} /> {err}
            </div>
          )}

          {rows.length === 0 ? (
            <div className="rounded-md border me-border px-3 py-4 text-[12px] me-text-mute">
              {loading ? 'Loading…'
                : statusFilter === 'pending'
                ? 'No pending suggestions. In shadow mode the check records here on the next run.'
                : `No ${statusFilter} suggestions.`}
            </div>
          ) : (
            <div className="rounded-md border me-border">
              {rows.map((s) => (
                <div
                  key={s.id}
                  className="border-b me-border px-3 py-2.5 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Type header — plain language + the two scores */}
                      <div className="flex items-center gap-2">
                        {s.type === 'misfiled'
                          ? <ArrowRightLeft size={14} className="shrink-0 me-text-mute" />
                          : <GitMerge size={14} className="shrink-0 me-text-mute" />}
                        <span className="text-[12.5px] font-semibold me-text">
                          {s.type === 'misfiled'
                            ? 'Email possibly misfiled in an existing matter'
                            : 'Possible instantiation of new matter by email belonging elsewhere'}
                        </span>
                        <span className="ml-auto shrink-0 text-[10.5px] me-text-mute tabular-nums">
                          conf {s.confidence ?? '–'} · identity {s.identity ?? '–'}
                        </span>
                      </div>

                      {/* The email in question */}
                      <div className="mt-1 flex items-center gap-2 text-[12px] min-w-0">
                        <span className="me-mono me-text shrink-0">{s.emal}</span>
                        <span className="truncate me-text-dim">{s.subject || '(no subject)'}</span>
                      </div>

                      {/* From → To, with role labels + the verb */}
                      <div className="mt-2 text-[12px]">
                        <div className="flex items-baseline gap-2 min-w-0">
                          <span className="w-16 shrink-0 text-[10px] uppercase tracking-wide me-text-mute">
                            {s.type === 'misfiled' ? 'now in' : 'new'}
                          </span>
                          <span className="me-text truncate">
                            {s.created_matter
                              ? `M-${String(s.created_matter.client_seq).padStart(3, '0')} ${s.created_matter.name}`
                              : 'new matter'}
                          </span>
                        </div>
                        <div className="my-0.5 pl-[4.5rem] text-[10.5px] me-text-mute">
                          ↓ {s.type === 'misfiled' ? 'move to' : 'merge into'}
                        </div>
                        <div className="flex items-baseline gap-2 min-w-0">
                          <span className="w-16 shrink-0 text-[10px] uppercase tracking-wide me-text-mute">
                            {s.type === 'misfiled' ? "thread's" : 'existing'}
                          </span>
                          <span className="font-semibold me-text truncate">
                            M-{String(s.target_matter.client_seq).padStart(3, '0')} {s.target_matter.name}
                          </span>
                        </div>
                      </div>

                      {/* Kind-shift warning — only when the kind actually changes */}
                      {s.kind_shift && (
                        <div
                          className="mt-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
                          style={{ background: 'rgba(186,117,23,0.16)', color: '#BA7517' }}
                          title="The incoming email's kind differs from the target matter's — could be a fork; review before applying."
                        >
                          <AlertTriangle size={11} />
                          kind changes: {s.issue_kind || '—'} → {s.target_kind || '—'} — check this isn't a fork
                        </div>
                      )}

                      {/* Why — readable, scenario-appropriate */}
                      <div className="mt-2 text-[12px] me-text-dim">
                        <span className="me-text-mute">Why: </span>
                        {s.type === 'misfiled' ? (
                          <>
                            {s.thread_total > 0
                              ? `${s.thread_in_target} of ${s.thread_total} of this email's thread-mates already live in the target matter; `
                              : 'this email shares its thread with the target matter; '}
                            its own match here was weak.
                            {s.shared_entities.length > 0 && (
                              <> Shared: <span className="me-text">{s.shared_entities.join(', ')}</span>.</>
                            )}
                          </>
                        ) : (
                          <>
                            same email thread
                            {s.thread_total > 1 ? ` (${s.thread_in_target} of ${s.thread_total} thread-mates in the target)` : ''}
                            {s.shared_entities.length > 0
                              ? <>, and they share: <span className="me-text">{s.shared_entities.join(', ')}</span>.</>
                              : '.'}
                          </>
                        )}
                      </div>

                      {s.status === 'dismissed' && s.resolution_note && (
                        <div className="mt-1 text-[11px] text-[color:var(--me-urgent)]">
                          <span className="me-text-mute">rejected because:</span> {s.resolution_note}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 me-chip text-[10px] self-center">{s.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
    </>
  )
}
